import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import FilterContainer from '@src/components/FilterContainer';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import { ResponsiveTable } from '@src/components/Table';
import JobForm from '@src/packages/jobs/components/JobForm';
import { INIT_JOBS_LIST_FILTERS } from '@src/packages/jobs/jobsListFilterConfig';
import { PaginationWithFirestore } from '@src/packages/utils';
import IntlMessages from '@src/util/IntlMessages';

import JobFilters from './components/JobFilters';
import { useJobs } from './hooks/useJobs';

const PAGE_SIZE = 10;

const JobsContent: React.FC = () => {
  const intl = useIntl();
  const {
    columns,
    paginationState,
    positionFormVisible,
    positionFormVisibleToggle,
    jobsLoading,
    loading,
    error,
    jobs,
    userJobIdRef,
    setRefreshTrigger,
    filters,
    setFilters,
    toggle,
    isOpen,
  } = useJobs();

  const filterPanel = useMemo(
    () => <JobFilters handleFilters={setFilters} filters={filters} />,
    [filters, setFilters],
  );

  const filterToggleLabel = intl.formatMessage({
    id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
  });

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={INIT_JOBS_LIST_FILTERS as FilterValues}
      >
        {filterPanel}
      </SecondaryPanel.Panel>
      <BoxContainer fullContent fixed bottomBorder>
        <FilterContainer
          title={<Title.Header value={<IntlMessages id="sidebar.configuration.job" />} />}
          actionButtons={[
            {
              label: filterToggleLabel,
              iconName: 'filter_alt',
              type: 'secondary',
              action: toggle,
              className: isOpen ? 'gx-border-primary' : '',
            },
            {
              label: <IntlMessages id="button.create" />,
              iconName: 'add',
              type: 'primary',
              action: positionFormVisibleToggle,
            },
          ]}
        />
      </BoxContainer>
      <FilterActiveTags.Container>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer fullContent loading={jobsLoading || loading} error={error}>
        <SecondaryPanel.Content>
          <ListViewportLayout
            viewportOffset={160}
            sidePanelOpen={isOpen}
            footer={
              <PaginationWithFirestore
                paginationState={paginationState}
                options={{
                  pageSize: PAGE_SIZE,
                  showTotal: false,
                }}
              />
            }
          >
            <ResponsiveTable
              dataSource={paginationState.paginatedData}
              columns={columns}
              loading={jobsLoading || loading}
            />
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>

      {positionFormVisible && (
        <JobForm
          id={userJobIdRef.current}
          onClose={() => {
            positionFormVisibleToggle();
            setRefreshTrigger(prev => prev + 1);
          }}
          visible={positionFormVisible}
          jobs={jobs}
        />
      )}
    </>
  );
};

const Jobs: React.FC = () => <JobsContent />;

export default Jobs;
